import * as React from 'react';
import { useState } from 'react';
import { useFormik } from 'formik';
import { ButtonWidget } from '../../widgets/buttons/ButtonWidget';
import { Input, InputWrapperWidget } from '../../widgets/inputs';
import { MAX_INPUT_FIELD_LENGTH, validateEmail, validateLength } from '../../utils/Validators';
import { Keys, useKeyPress } from '../../hooks/useKeyPress';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSystem } from '../../hooks/useSystem';
import styled from '@emotion/styled';

export interface EnterEmailPayload {
  email: string;
}

export interface EnterEmailWidgetProps {
  payload?: Partial<EnterEmailPayload>;
  submit: (payload: EnterEmailPayload) => Promise<void>;
  onValueChanged?: (value: string | null) => void;
  onReCaptchaV2?: (token: string | null) => void;
  signupError?: { retryable?: boolean; message?: string | JSX.Element };
  fallbackCaptcha: boolean;
}

export const EnterEmailWidget: React.FC<EnterEmailWidgetProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const system = useSystem();
  const formik = useFormik({
    initialValues: {
      email: props.payload?.email || ''
    },
    validate: (values) => {
      const errors: { email?: string } = {};
      if (!values.email) {
        errors.email = 'This field is required';
      } else if (!validateEmail(values.email)) {
        errors.email = 'Please enter a valid email address';
      }
      if (!validateLength(values.email)) {
        errors.email = `Email address must be less than ${MAX_INPUT_FIELD_LENGTH} characters`;
      }
      return errors;
    },
    onSubmit: async (event) => {
      if ((!!props.signupError?.message && props.signupError?.retryable === false) || loading) {
        return;
      }
      setLoading(true);
      await props.submit({
        email: event.email
      });
      setLoading(false);
    }
  });

  useKeyPress(Keys.ENTER, {
    onKeyPressed: () => {
      formik.submitForm();
    }
  });

  const getFooter = () => {
    if (props.fallbackCaptcha === true) {
      return [
        <ReCAPTCHA theme={'dark'} sitekey={system.context.recaptcha.v2_site_key} onChange={props.onReCaptchaV2} />
      ];
    }
    return [
      <ButtonWidget
        key={'try-button'}
        label="Sign up with email"
        disabled={
          (!!props.signupError?.message && props.signupError?.retryable === false) ||
          !formik.values.email ||
          !!formik.errors.email
        }
        loadingOverride={loading}
        action={async (event, loading) => {
          loading(true);
          await formik.submitForm();
          loading(false);
        }}
      />
    ];
  };

  return (
    <>
      <S.InputWrapper error={formik.touched.email && (props.signupError?.message || formik.errors.email)}>
        <Input
          key={'email'}
          placeholder="Email"
          name={'email'}
          type={'email'}
          autoCapitalize={'none'}
          value={formik.values.email}
          error={formik.touched.email && !!(props.signupError?.message || formik.errors.email)}
          onChange={(e) => {
            props.onValueChanged?.(e.target?.value);
            formik.handleChange(e);
          }}
          onBlur={(e) => {
            formik.handleBlur(e);
          }}
        />
      </S.InputWrapper>
      {getFooter()}
    </>
  );
};

namespace S {
  export const InputWrapper = styled(InputWrapperWidget)`
    margin-bottom: 10px;
  `;
}
